import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, Clock, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import axios from 'axios';

interface AttoData {
  targa: string;
  tipoAtto: string;
  dataCompletamento: string;
  numeroCartellaPRA?: string;
  attoVenditaPDF: {
    filename: string;
    originalName: string;
    path: string;
    uploadDate: string;
  };
  richiedente: {
    businessName: string;
  };
  studioAssegnato: {
    businessName: string;
  };
  tokenScadenza: string;
}

const AttoVenditorePublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [atto, setAtto] = useState<AttoData | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  useEffect(() => {
    loadAtto();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [token]);

  const loadAtto = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/atti-svincolo/venditore/${token}`);
      
      if (response.data.success) {
        setAtto(response.data.data);
      }
    } catch (err: any) {
      console.error('Errore caricamento atto:', err);
      
      if (err.response?.status === 410) {
        setExpired(true);
        setError('Il link è scaduto. Il documento era disponibile per 48 ore dal completamento della pratica.');
      } else if (err.response?.status === 404) {
        setError('Link non valido o documento non trovato.');
      } else {
        setError('Errore durante il caricamento del documento. Riprova più tardi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (token) {
      // Usa l'endpoint dedicato per il download con il token del venditore
      window.open(`${API_BASE_URL}/api/atti-svincolo/venditore/${token}/download`, '_blank');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = () => {
    if (!atto?.tokenScadenza) return '';
    
    const now = new Date().getTime();
    const scadenza = new Date(atto.tokenScadenza).getTime();
    const diff = scadenza - now;
    
    if (diff <= 0) return 'Scaduto';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} giorn${days > 1 ? 'i' : 'o'} rimanent${days > 1 ? 'i' : 'e'}`;
    }
    
    return `${hours}h ${minutes}m rimanenti`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento documento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            {expired ? (
              <Clock className="w-20 h-20 text-orange-500 mx-auto mb-4" />
            ) : (
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {expired ? 'Link Scaduto' : 'Errore'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            {expired && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  Per ottenere nuovamente il documento, contatta lo studio che ha gestito la pratica.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!atto) {
    return null;
  }

  const timeRemaining = getTimeRemaining();
  const isExpiringSoon = atto.tokenScadenza && 
    (new Date(atto.tokenScadenza).getTime() - new Date().getTime()) < 24 * 60 * 60 * 1000;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden mb-6">
          <div className="bg-gradient-to-t from-blue-600 to-indigo-500 p-8 text-white text-center">
            <FileText className="w-10 h-10 mx-auto mb-4" />
            <h1 className="text-xl font-bold mb-2">Atto di Vendita</h1>
            <p className="text-blue-100">Targa: {atto.targa}</p>
          </div>

          {/* Alert Scadenza */}
          {isExpiringSoon && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 m-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-1">
                    ⏰ Link in Scadenza
                  </h3>
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    Scarica il documento il prima possibile. {timeRemaining}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Contenuto */}
          <div className="p-8">
            {/* Info Documento */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                Dettagli Documento
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Targa</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{atto.targa}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Tipo Atto</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {atto.tipoAtto === 'articolo56' ? 'Art. 56' : 
                     atto.tipoAtto === 'miniPassaggio' ? 'Mini Passaggio' : 
                     'Passaggio Regolare'}
                  </span>
                </div>
                {atto.numeroCartellaPRA && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400">Numero Cartella PRA</span>
                    <span className="font-mono font-bold text-gray-900 dark:text-white">
                      {atto.numeroCartellaPRA}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Data Completamento</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(atto.dataCompletamento)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 dark:text-gray-400">Link valido fino al</span>
                  <span className="font-semibold text-orange-600 dark:text-orange-400">
                    {formatDateTime(atto.tokenScadenza)}
                  </span>
                </div>
              </div>
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 text-lg"
            >
              <Download className="w-6 h-6" />
              Scarica Atto di Vendita
            </button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
              {atto.attoVenditaPDF.originalName}
            </p>
          </div>
        </div>

        {/* Info Footer */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">ℹ️ Informazioni</h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li>• Questo documento è stato preparato da: <strong>{atto.studioAssegnato.businessName}</strong></li>
            <li>• Richiesto da: <strong>{atto.richiedente.businessName}</strong></li>
            <li>• Il link è personale e non deve essere condiviso</li>
            <li>• Conserva il documento in un luogo sicuro</li>
            <li>• Dopo la scadenza del link non sarà più possibile scaricare il documento</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AttoVenditorePublicPage;

